import React, { useEffect, useRef, useState } from 'react';
import './tutorial.css';
import { useAppStore } from '@/hooks/useAppStore';
import { useTranslation } from '@/lib/translate';
import { Language } from '@/types';

interface TutorialOverlayProps {
    onComplete: () => void;
    onMicrophoneEnabled?: () => void;
    isRedirecting?: boolean;
}

const TutorialOverlay: React.FC<TutorialOverlayProps> = ({ onComplete, onMicrophoneEnabled, isRedirecting = false }) => {
    const setPreferredLanguage = useAppStore((state) => state.setPreferredLanguage);
    const preferredLanguage = useAppStore((state) => state.preferredLanguage);
    const t = useTranslation(preferredLanguage || 'en');

    const [isVisible, setIsVisible] = useState(true);
    const [languageSelected, setLanguageSelected] = useState(false);
    const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
    const [chatAnimationStarted, setChatAnimationStarted] = useState(false);

    // 0: Intro, 1: Language, 2: Mic, 3: Start
    const [currentStep, setCurrentStep] = useState(0);
    const touchStartY = useRef<number | null>(null);

    // Refs for animation elements
    const typingIndicatorRef = useRef<HTMLDivElement>(null);
    const bubble1Ref = useRef<HTMLDivElement>(null);
    const bubble2Ref = useRef<HTMLDivElement>(null);
    const bubble3Ref = useRef<HTMLDivElement>(null);
    const bubble3_5Ref = useRef<HTMLDivElement>(null);
    const bubble4Ref = useRef<HTMLDivElement>(null);
    const bubble5Ref = useRef<HTMLDivElement>(null);
    const bubble6Ref = useRef<HTMLDivElement>(null);
    const bubble7Ref = useRef<HTMLDivElement>(null);
    const bubble8Ref = useRef<HTMLDivElement>(null);
    const languageOptionsRef = useRef<HTMLDivElement>(null);
    const scrollIndicatorLanguageRef = useRef<HTMLDivElement>(null);
    const scrollIndicatorMicRef = useRef<HTMLDivElement>(null);

    const startChatAnimation = () => {
        if (chatAnimationStarted) return;
        setChatAnimationStarted(true);

        const show = (ref: React.RefObject<HTMLDivElement | null>) => {
            if (ref.current) ref.current.classList.add('visible');
        };
        const hide = (ref: React.RefObject<HTMLDivElement | null>) => {
            if (ref.current) ref.current.classList.remove('visible');
        };

        // Bubble 1
        setTimeout(() => show(typingIndicatorRef), 20);
        setTimeout(() => {
            hide(typingIndicatorRef);
            show(bubble1Ref);
        }, 200);

        // Bubble 2
        setTimeout(() => show(typingIndicatorRef), 700);
        setTimeout(() => {
            hide(typingIndicatorRef);
            show(bubble2Ref);
        }, 1900);

        // Bubble 3
        setTimeout(() => show(typingIndicatorRef), 2200);
        setTimeout(() => {
            hide(typingIndicatorRef);
            show(bubble3Ref);
        }, 3000);

        // Bubble 3.5
        setTimeout(() => show(typingIndicatorRef), 3300);
        setTimeout(() => {
            hide(typingIndicatorRef);
            show(bubble3_5Ref);
        }, 3900);

        // Language Options
        setTimeout(() => show(languageOptionsRef), 4200);
    };

    // Trigger chat animation when entering step 1
    useEffect(() => {
        if (currentStep === 1 && !isRedirecting) {
            startChatAnimation();
        }
    }, [currentStep, isRedirecting]);

    const nextStep = () => {
        if (currentStep === 0) {
            setCurrentStep(1);
        } else if (currentStep === 1 && languageSelected) {
            setCurrentStep(2);
        } else if (currentStep === 2 && microphoneEnabled) {
            setCurrentStep(3);
        }
    };

    // Handle wheel and touch events for navigation
    useEffect(() => {
        if (isRedirecting) return;

        const handleWheel = (e: WheelEvent) => {
            if (e.deltaY > 50) {
                nextStep();
            }
        };

        const handleTouchStart = (e: TouchEvent) => {
            touchStartY.current = e.touches[0].clientY;
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (touchStartY.current === null) return;
            const touchEndY = e.touches[0].clientY;
            const diff = touchStartY.current - touchEndY;

            if (diff > 50) { // Swipe up
                nextStep();
                touchStartY.current = null; // Reset to prevent multiple triggers
            }
        };

        window.addEventListener('wheel', handleWheel);
        window.addEventListener('touchstart', handleTouchStart);
        window.addEventListener('touchmove', handleTouchMove);

        return () => {
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, [currentStep, languageSelected, microphoneEnabled, isRedirecting]);

    const handleLanguageSelect = (lang: string) => {
        if (languageSelected) return;

        let languageCode: Language = 'en';
        
        if (lang === 'japanese') {
            languageCode = 'ja';
        } else if (lang === 'chinese') {
            languageCode = 'zh';
        }

        // Save language to global store
        setPreferredLanguage(languageCode);
        
        // Set language as selected immediately to keep button highlighted
        setLanguageSelected(true);

        const show = (ref: React.RefObject<HTMLDivElement | null>) => {
            if (ref.current) ref.current.classList.add('visible');
        };
        const hide = (ref: React.RefObject<HTMLDivElement | null>) => {
            if (ref.current) ref.current.classList.remove('visible');
        };

        // For Japanese, show 5 bubbles in sequence
        if (languageCode === 'ja') {
            setTimeout(() => show(typingIndicatorRef), 50);
            setTimeout(() => { hide(typingIndicatorRef); show(bubble4Ref); }, 200);
            
            setTimeout(() => show(typingIndicatorRef), 1200);
            setTimeout(() => { hide(typingIndicatorRef); show(bubble5Ref); }, 2400);
            
            setTimeout(() => show(typingIndicatorRef), 3400);
            setTimeout(() => { hide(typingIndicatorRef); show(bubble6Ref); }, 4200);
            
            setTimeout(() => show(typingIndicatorRef), 5200);
            setTimeout(() => { hide(typingIndicatorRef); show(bubble7Ref); }, 5800);
            
            setTimeout(() => show(typingIndicatorRef), 6800);
            setTimeout(() => { hide(typingIndicatorRef); show(bubble8Ref); }, 7400);
            
            setTimeout(() => {
                if (scrollIndicatorLanguageRef.current) {
                    const wrapper = scrollIndicatorLanguageRef.current.querySelector('.scroll-indicator-wrapper') as HTMLElement;
                    if (wrapper) {
                        wrapper.style.display = 'flex';
                        wrapper.style.animation = 'fadeInBounce 1s ease forwards';
                    }
                }
            }, 7600);
        } else {
            // For other languages, show single bubble
            setTimeout(() => show(typingIndicatorRef), 50);
            setTimeout(() => {
                hide(typingIndicatorRef);
                show(bubble4Ref);
            }, 200);
            setTimeout(() => {
                if (scrollIndicatorLanguageRef.current) {
                    const wrapper = scrollIndicatorLanguageRef.current.querySelector('.scroll-indicator-wrapper') as HTMLElement;
                    if (wrapper) {
                        wrapper.style.display = 'flex';
                        wrapper.style.animation = 'fadeInBounce 1s ease forwards';
                    }
                }
            }, 400);
        }
    };

    const handleEnableMic = async () => {
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            setMicrophoneEnabled(true);
            // Set flag to indicate mic permission was granted in tutorial
            localStorage.setItem('chotto_mic_permission_granted', 'true');
            // Notify parent to start loading app in background
            onMicrophoneEnabled?.();
            setTimeout(() => {
                if (scrollIndicatorMicRef.current) {
                    scrollIndicatorMicRef.current.style.display = 'flex';
                    const wrapper = scrollIndicatorMicRef.current.querySelector('.scroll-indicator-wrapper') as HTMLElement;
                    if (wrapper) {
                        wrapper.style.display = 'flex';
                        wrapper.style.animation = 'fadeInBounce 1s ease forwards';
                    }
                }
            }, 500);
        } catch (err) {
            console.error('Microphone access denied:', err);
            alert(t('Microphone access is required to use Chotto. Please enable microphone permissions in your browser settings.'));
        }
    };

    const handleStart = () => {
        setIsVisible(false);
        setTimeout(onComplete, 600);
    };

    const handleSkip = () => {
        // Detect device language and set it (fallback to English if not ja/zh/en)
        const deviceLang = (navigator.language || (navigator as any).userLanguage || 'en').toLowerCase();
        let languageCode: Language = 'en';
        if (deviceLang.startsWith('ja')) {
            languageCode = 'ja';
        } else if (deviceLang.startsWith('zh')) {
            languageCode = 'zh';
        }
        setPreferredLanguage(languageCode);

        // Complete the tutorial (mic permission will be triggered via global tap listener)
        handleStart();
    };

    // Tagline animation logic with language detection
    const getOrderedTaglines = () => {
        const taglines = {
            en: "Say less, Connect More",
            ja: "ちょっと話す、もっとつながる",
            zh: "言语更少，连接更多"
        };

        const languages = Object.keys(taglines) as Array<'en' | 'ja' | 'zh'>;

        // Detect language (default 'en')
        const detected =
            languages.find(l => navigator.language.toLowerCase().startsWith(l)) ?? 'en';

        // Sort priority: detected first, then English, then the rest
        const priority = (lang: string) =>
            lang === detected ? 0 :
            lang === 'en' ? 1 :
            2;

        return languages
            .sort((a, b) => priority(a) - priority(b))
            .map(lang => taglines[lang]);
    };

    const TAGLINES = getOrderedTaglines();
    const [taglineIndex, setTaglineIndex] = useState(0);
    const [taglineState, setTaglineState] = useState<'initial' | 'entering' | 'active' | 'exiting'>('initial');

    useEffect(() => {
        if (currentStep !== 0 || !isVisible) return;

        let timeoutId: NodeJS.Timeout;

        const animate = () => {
            if (taglineState === 'initial') {
                // Initial delay before starting the loop
                timeoutId = setTimeout(() => {
                    setTaglineState('entering');
                }, 2000);
            } else if (taglineState === 'entering') {
                // Start enter animation
                timeoutId = setTimeout(() => {
                    setTaglineState('active');
                }, 100);
            } else if (taglineState === 'active') {
                // Wait for stay duration then exit
                timeoutId = setTimeout(() => {
                    setTaglineState('exiting');
                }, 2000 + 1500); // 2s persist + 1.5s enter transition
            } else if (taglineState === 'exiting') {
                // Wait for exit transition + empty pause then next tagline
                timeoutId = setTimeout(() => {
                    setTaglineIndex((prev) => (prev + 1) % TAGLINES.length);
                    setTaglineState('entering');
                }, 250); // 0.3s exit transition + 0.5s pause
            }
        };

        animate();

        return () => clearTimeout(timeoutId);
    }, [taglineState, currentStep, isVisible]);

    // Reset loop if we return to step 0
    useEffect(() => {
        if (currentStep === 0 && isVisible) {
            setTaglineIndex(0);
            setTaglineState('initial');
        }
    }, [currentStep, isVisible]);

    const getStepClass = (stepIndex: number) => {
        if (currentStep === stepIndex) return 'active';
        if (currentStep > stepIndex) return 'prev';
        return 'next';
    };

    if (!isVisible) return null;

    if (isRedirecting) {
        return <div className="tutorial-background" style={{ opacity: 1 }}></div>;
    }

    return (
        <>
            <div className="tutorial-background" style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.6s ease' }}></div>
            <button className="skip-tutorial-btn" onClick={handleSkip}>{t('Skip')}</button>
            
            <div className={`tutorial-steps-container ${!isVisible ? 'hidden' : ''}`} style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.6s ease' }}>
                
                {/* Step 0: Intro */}
                <div className={`tutorial-step ${getStepClass(0)}`}>
                    <div className="tutorial-overlay">
                        <div className="tutorial-content">
                            <img className="tutorial-logo" src="/assets/img/chotto-logo.png" alt="Chotto Chat" />
                            <h2 className={`tutorial-tagline ${taglineState}`}>
                                {TAGLINES[taglineIndex]}
                            </h2>
                            <div className="scroll-indicator" onClick={nextStep}>
                                <span className="scroll-text">{t('Scroll Down')}</span>
                                <div className="scroll-arrow"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step 1: Language */}
                <div className={`tutorial-step ${getStepClass(1)}`}>
                    <div className="tutorial-language-content-block">
                        <div className="tutorial-language-content">
                            <div className="language-chat-bubble" id="bubble1" ref={bubble1Ref}>
                                <div className="language-chat-text">Hi there 👋</div>
                            </div>
                            <div className="language-chat-bubble" id="bubble2" ref={bubble2Ref}>
                                <div className="language-chat-text">Do you speak English?</div>
                            </div>
                            <div className="language-chat-bubble" id="bubble3" ref={bubble3Ref}>
                                <div className="language-chat-text">日本語ネイティブですか？</div>
                            </div>
                            <div className="language-chat-bubble" id="bubble3_5" ref={bubble3_5Ref}>
                                <div className="language-chat-text">或者你会说中文？</div>
                            </div>
                            <div className="tutorial-language-options" id="languageOptions" ref={languageOptionsRef}>
                                <button className={`language-option-btn ${preferredLanguage === 'en' && languageSelected ? 'selected' : ''}`} onClick={() => handleLanguageSelect('english')}>English</button>
                                <button className={`language-option-btn ${preferredLanguage === 'ja' && languageSelected ? 'selected' : ''}`} onClick={() => handleLanguageSelect('japanese')}>日本語</button>
                                <button className={`language-option-btn ${preferredLanguage === 'zh' && languageSelected ? 'selected' : ''}`} onClick={() => handleLanguageSelect('chinese')}>中文</button>
                            </div>
                            {preferredLanguage === 'ja' ? (
                                <>
                                    <div className="language-chat-bubble" id="bubble4" ref={bubble4Ref}>
                                        <div className="language-chat-text">わあ、すごい！お会いできて嬉しいです！私はちょっとです！</div>
                                    </div>
                                    <div className="language-chat-bubble" id="bubble5" ref={bubble5Ref}>
                                        <div className="language-chat-text">私があなたの言葉を、もう一つの言語でスムーズに話せるようにお手伝いします。</div>
                                    </div>
                                    <div className="language-chat-bubble" id="bubble6" ref={bubble6Ref}>
                                        <div className="language-chat-text" dangerouslySetInnerHTML={{ __html: '<strong>あなたの声</strong>で話してください！' }} />
                                    </div>
                                    <div className="language-chat-bubble" id="bubble7" ref={bubble7Ref}>
                                        <div className="language-chat-text">そして、それを素早く正確に別の言語に翻訳します。</div>
                                    </div>
                                    <div className="language-chat-bubble" id="bubble8" ref={bubble8Ref}>
                                        <div className="language-chat-text">次に、マイクへのアクセスが必要です。あなたの声を聞けるように！</div>
                                    </div>
                                </>
                            ) : (
                                <div className="language-chat-bubble" id="bubble4" ref={bubble4Ref}>
                                    <div className="language-chat-text">{t("Oh Amazing! Nice to meet you! I'm Chotto!")}</div>
                                </div>
                            )}
                            <div className="typing-indicator" id="typingIndicator" ref={typingIndicatorRef}>
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="scroll-indicator-language-block"
                        id="scrollIndicatorLanguage"
                        ref={scrollIndicatorLanguageRef}
                        onClick={nextStep}
                    >
                        <div className="scroll-indicator-wrapper" style={{ display: 'none', flexDirection: 'column', alignItems: 'center' }}>
                            <span className="scroll-text">{t('Scroll Down')}</span>
                            <div className="scroll-arrow"></div>
                        </div>
                    </div>
                </div>

                {/* Step 2: Microphone */}
                <div className={`tutorial-step ${getStepClass(2)}`}>
                    <div className="tutorial-mic-section">
                        <div className="tutorial-mic-icon">
                            <svg viewBox="0 0 24 24" fill="white">
                                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                            </svg>
                        </div>
                        <h3 className="tutorial-mic-title">{t('Enable Microphone')}</h3>
                        <p className="tutorial-mic-description">
                            {t('Chotto needs microphone access to enable real-time voice translation.')}
                        </p>
                        <button
                            className={`enable-mic-button ${microphoneEnabled ? 'enabled' : ''}`}
                            onClick={handleEnableMic}
                        >
                            {microphoneEnabled ? (
                                <>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ width: '20px', height: '20px', display: 'inline-block', marginRight: '8px' }}>
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    {t('Enabled')}
                                </>
                            ) : t('Enable Microphone')}
                        </button>
                        <div
                            className="scroll-indicator-mic"
                            id="scrollIndicatorMic"
                            ref={scrollIndicatorMicRef}
                            style={{ 
                                position: 'absolute',
                                bottom: '4rem',
                                width: '100%',
                                textAlign: 'center',
                                display: 'flex'
                            }}
                            onClick={nextStep}
                        >
                            <div className="scroll-indicator-wrapper" style={{ display: 'none', flexDirection: 'column', alignItems: 'center' }}>
                                <span className="scroll-text">{t('Scroll Down')}</span>
                                <div className="scroll-arrow"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step 3: Start */}
                <div className={`tutorial-step ${getStepClass(3)}`}>
                    <div className="tutorial-start-section">
                        <p className="tutorial-description">
                            {t('Real-time translation chat that breaks language barriers. Speak naturally, and let Chotto handle the rest.')}
                        </p>
                        <button className="start-button" onClick={handleStart}>{t('Start')}</button>
                    </div>
                </div>

            </div>
        </>
    );
};

export default TutorialOverlay;
